import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import api from "../../Services/api";
import { errorManagement } from "../../Services/errorManagement";
import { HomeAppContext } from "../Home";

const clampWeight = (value) => {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
};

const buildDefaultWeights = (engines) =>
  (engines || []).map((engine, index) => ({
    engineId: engine.id,
    weightPercent: index === 0 ? 100 : 0
  }));

const rebalanceWeights = (currentWeights, targetEngineId, nextValue) => {
  if (!Array.isArray(currentWeights) || currentWeights.length === 0) {
    return currentWeights;
  }

  if (currentWeights.length === 1) {
    return currentWeights.map((entry) => ({
      ...entry,
      weightPercent: 100
    }));
  }

  const targetValue = clampWeight(nextValue);
  const updated = currentWeights.map((entry) =>
    entry.engineId === targetEngineId
      ? { ...entry, weightPercent: targetValue }
      : { ...entry }
  );

  const total = updated.reduce(
    (sum, entry) => sum + (parseInt(entry.weightPercent, 10) || 0),
    0
  );

  if (total === 100) {
    return updated;
  }

  if (total > 100) {
    let excess = total - 100;
    const others = updated
      .filter((entry) => entry.engineId !== targetEngineId)
      .sort((a, b) => (b.weightPercent || 0) - (a.weightPercent || 0));

    for (const entry of others) {
      if (excess <= 0) break;
      const current = parseInt(entry.weightPercent, 10) || 0;
      const reduction = Math.min(current, excess);
      entry.weightPercent = current - reduction;
      excess -= reduction;
    }
    return updated;
  }

  let deficit = 100 - total;
  const others = updated
    .filter((entry) => entry.engineId !== targetEngineId)
    .sort((a, b) => (a.weightPercent || 0) - (b.weightPercent || 0));

  for (const entry of others) {
    if (deficit <= 0) break;
    const current = parseInt(entry.weightPercent, 10) || 0;
    const room = 100 - current;
    if (room <= 0) continue;
    const addition = Math.min(room, deficit);
    entry.weightPercent = current + addition;
    deficit -= addition;
  }

  return updated;
};

const RoutingWeights = ({ groupId, engines = [], allowActions = false }) => {
  const { homeDispatch } = useContext(HomeAppContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [weights, setWeights] = useState(buildDefaultWeights(engines));

  useEffect(() => {
    if (!groupId) return;

    setLoading(true);
    setWeights(buildDefaultWeights(engines));
    setIsEnabled(false);

    api
      .get(`/api/ai-prompts/EngineGroupRoutingConfig/${groupId}`)
      .then((response) => {
        const data = response?.data || {};
        const serverWeights = Array.isArray(data.engineWeights)
          ? data.engineWeights
          : [];
        const weightMap = new Map(
          serverWeights.map((entry) => [entry.engineId, entry.weightPercent])
        );

        const nextWeights = (engines || []).map((engine, index) => {
          const raw = weightMap.has(engine.id)
            ? weightMap.get(engine.id)
            : index === 0
            ? 100
            : 0;
          return {
            engineId: engine.id,
            weightPercent: clampWeight(parseInt(raw, 10))
          };
        });

        setWeights(nextWeights);
        setIsEnabled(Boolean(data.isEnabled));
      })
      .catch((error) => {
        if (error?.response?.status !== 404) {
          errorManagement.formErrors(error, homeDispatch);
        }
      })
      .finally(() => setLoading(false));
  }, [groupId, engines, homeDispatch]);

  const totalWeight = useMemo(
    () =>
      weights.reduce(
        (sum, entry) => sum + (parseInt(entry.weightPercent, 10) || 0),
        0
      ),
    [weights]
  );

  const hasInvalidWeights = useMemo(
    () =>
      weights.some((entry) => {
        const value = parseInt(entry.weightPercent, 10);
        return Number.isNaN(value) || value < 0 || value > 100;
      }),
    [weights]
  );

  const handleWeightChange = (engineId, value) => {
    const parsed = parseInt(value, 10);
    setWeights((current) => rebalanceWeights(current, engineId, parsed));
  };

  const handleSetSingle = (engineId) => {
    setWeights((current) =>
      current.map((entry) => ({
        ...entry,
        weightPercent: entry.engineId === engineId ? 100 : 0
      }))
    );
  };

  const handleSave = () => {
    if (!allowActions) return;

    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Saving routing weights", loading: true }
    });

    setSaving(true);
    const payload = {
      groupId,
      isEnabled,
      engineWeights: weights.map((entry) => ({
        engineId: entry.engineId,
        weightPercent: parseInt(entry.weightPercent, 10) || 0
      }))
    };

    api
      .put(`/api/ai-prompts/EngineGroupRoutingConfig/${groupId}`, payload)
      .then(() => {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: "Routing weights updated",
            type: "success"
          }
        });
      })
      .catch((error) => {
        errorManagement.formErrors(error, homeDispatch);
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <Box p={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box p={2} sx={{ borderTop: "1px solid #eee" }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item sm={12}>
          <Typography variant="subtitle1" fontWeight="bold">
            Routing Weights
          </Typography>
        </Grid>
        <Grid item sm={12}>
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(event) => setIsEnabled(event.target.checked)}
                disabled={!allowActions}
              />
            }
            label="Enable group routing"
          />
        </Grid>
        {engines.length === 0 ? (
          <Grid item sm={12}>
            <Typography variant="body2">No engines in this group.</Typography>
          </Grid>
        ) : (
          engines.map((engine) => {
            const current =
              weights.find((entry) => entry.engineId === engine.id) ||
              { weightPercent: 0 };
            return (
              <Grid
                item
                sm={12}
                key={engine.id}
                container
                spacing={2}
                alignItems="center"
              >
                <Grid item sm={5}>
                  <Typography variant="body2">
                    {engine.text}
                    {engine.description ? ` (${engine.description})` : ""}
                  </Typography>
                </Grid>
                <Grid item sm={3}>
                  <TextField
                    type="number"
                    label="Weight %"
                    size="small"
                    fullWidth
                    value={current.weightPercent}
                    onChange={(event) =>
                      handleWeightChange(engine.id, event.target.value)
                    }
                    inputProps={{ min: 0, max: 100 }}
                    disabled={!allowActions}
                  />
                </Grid>
                <Grid item sm={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleSetSingle(engine.id)}
                    disabled={!allowActions}
                  >
                    100%
                  </Button>
                </Grid>
              </Grid>
            );
          })
        )}
        <Grid item sm={12}>
          <Typography
            variant="body2"
            sx={{
              color: totalWeight === 100 && !hasInvalidWeights ? "success.main" : "error.main"
            }}
          >
            Total: {totalWeight}% (must equal 100%)
          </Typography>
        </Grid>
        <Grid item sm={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!allowActions || saving || hasInvalidWeights || totalWeight !== 100}
          >
            Save Weights
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RoutingWeights;
